import { formatInTimeZone } from "date-fns-tz";
import { ReactNode, useEffect, useState } from "react";
import {
  Button,
  Div,
  Field,
  Icon,
  Progress,
  Section,
  Span,
  Table,
} from "trunx";
import {
  decimalPlaces,
  formatUnit,
  useLocaleContext,
} from "../contexts/LocaleContext";
import convert, { isVolumeFlowRateUnit } from "../convert";
import {
  LeafletPanelBody,
  LeafletSensorPanelHeading,
} from "../shared/LeafletPanel";

import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LengthUnits } from "convert-units/definitions/length";
import { VolumeFlowRateUnits } from "convert-units/definitions/volumeFlowRate";
import { subDays } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import moment from "moment-timezone";
import CsvDownloader from "react-csv-downloader";
import DateRangePicker from "../DateRangePicker";
import { isPrecipSensor } from "../models/PrecipSensor";
import { TabularData } from "../models/SensorBase";
import { ISensor } from "../models/Sensors";
import { observer } from "mobx-react-lite";

const forCsvDownloader = (
  data: TabularData,
  tz: string | undefined,
  toUnit: LengthUnits,
  ratedUnit: VolumeFlowRateUnits,
) => {
  let formatDate: (date: Date) => string;
  let formatTime: (date: Date) => string;
  if (tz === undefined) {
    formatDate = (date: Date) => date.toISOString().split("T")[0];
    formatTime = (date: Date) => date.toISOString().split("T")[1].split(".")[0];
  } else {
    formatDate = (date: Date) => formatInTimeZone(date, tz, "yyyy-MM-dd");
    formatTime = (date: Date) => formatInTimeZone(date, tz, "HH:mm:ss");
  }

  if (data.type === "OneColumn") {
    return Array.from(data.rows.entries()).map(([date, value]) => ({
      date: formatDate(date),
      time: formatTime(date),
      value: convert(value).from(data.unit).to(toUnit).toString(),
    }));
  } else {
    return Array.from(data.rows.entries()).map(([date, [value, rated]]) => ({
      date: formatDate(date),
      time: formatTime(date),
      value: convert(value).from(data.units[0]).to(toUnit).toString(),
      rated: convert(rated).from(data.units[1]).to(ratedUnit).toString(),
    }));
  }
};

interface TabularDataPaneProps {
  sensor: ISensor;
}
const TabularDataPane = ({ sensor }: TabularDataPaneProps) => {
  const {
    lang,
    units,
    dict: {
      pane: { tabular: dict },
    },
  } = useLocaleContext();

  const today = new Date();
  const [startDate, setStartDate] = useState(subDays(today, 7));
  const [endDate, setEndDate] = useState(today);

  useEffect(() => {
    sensor.fetchTabular(startDate, endDate);
  }, [endDate, sensor, startDate]);

  const pickerProps = { startDate, setStartDate, endDate, setEndDate };

  const data = sensor.tabularData;

  const isPrecip = isPrecipSensor(sensor);
  const valueHead = isPrecip ? dict.precipitation : dict.stage;
  const toUnit = isPrecip ? units.precip.total : units.stream.stage;
  const toUnitRated = units.stream.discharge;

  const gotData = !(data === undefined || data === "loading");

  const CsvButtonWrapper = ({ children }: { children: ReactNode }) =>
    gotData ? (
      <CsvDownloader
        filename={`${sensor.id.replaceAll("/", "_")}.csv`}
        datas={forCsvDownloader(data, sensor.timezone, toUnit, toUnitRated)}
        columns={[
          { id: "date", displayName: dict.date },
          { id: "time", displayName: dict.time + ` (${sensor.timezone})` },
          { id: "value", displayName: valueHead + ` (${formatUnit(toUnit)})` },
        ].concat(
          data.type === "TwoColumns"
            ? [
                {
                  id: "rated",
                  displayName: dict.discharge + ` (${formatUnit(toUnitRated)})`,
                },
              ]
            : [],
        )}
      >
        {children}
      </CsvDownloader>
    ) : (
      <>{children}</>
    );

  const TabularPanel = ({ children }: { children: ReactNode }) => (
    <Div bulma="panel">
      <LeafletSensorPanelHeading sensor={sensor} action="back" />

      <LeafletPanelBody isFullHeight>
        <Section>
          <DateRangePicker {...pickerProps} />
          <CsvButtonWrapper>
            <Field isGrouped className="is-grouped-centered">
              <Button bulma="is-primary" disabled={!gotData}>
                {dict.csv}
              </Button>
            </Field>
          </CsvButtonWrapper>
        </Section>
        {children}
      </LeafletPanelBody>
    </Div>
  );

  if (!gotData) {
    return (
      <TabularPanel>
        <Section>
          <Progress
            bulma="m-auto"
            style={{ width: "50%" }}
            max={100}
          ></Progress>
        </Section>
      </TabularPanel>
    );
  } else {
    let zoneWarn = <></>;
    if (sensor.timezone !== undefined) {
      const sensorZone = moment.tz.zone(sensor.timezone);
      const browserZone = moment.tz.zone(moment.tz.guess());

      if (sensorZone?.offsets !== browserZone?.offsets) {
        zoneWarn = (
          <Section bulma="has-text-centered">
            <Span bulma="has-text-warning">
              <Icon>
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </Icon>
              <span>{dict.zoneWarning(sensor.timezone)}</span>
            </Span>
          </Section>
        );
      }
    }

    const TableHead = ({ data }: { data: TabularData }) => {
      const hasDischarge = data.type === "TwoColumns";
      return (
        <thead>
          <tr>
            <th>{dict.date}</th>
            <th>{dict.time}</th>
            <th>{valueHead}</th>
            {hasDischarge && <th>{dict.discharge}</th>}
          </tr>
        </thead>
      );
    };

    const TableBody = ({ data }: { data: TabularData }) => {
      if (data.type === "OneColumn") {
        const fromUnit = data.unit;

        const rows = Array.from(data.rows.entries())
          .sort(([left], [right]) => (left < right ? 1 : -1))
          .map(([date, value]) => ({
            date: sensor.timezone
              ? utcToZonedTime(date, sensor.timezone)
              : date,
            value,
          }));
        return (
          <tbody>
            {rows.map(({ date, value }) => (
              <tr key={date.getTime()}>
                <td>{date.toLocaleDateString(lang)}</td>
                <td>{date.toLocaleTimeString(lang)}</td>
                <td>
                  {convert(value)
                    .from(fromUnit)
                    .to(toUnit)
                    .toFixed(decimalPlaces(toUnit))}{" "}
                  {formatUnit(toUnit)}
                </td>
              </tr>
            ))}
          </tbody>
        );
      } else {
        const rows = Array.from(data.rows.entries())
          .sort(([left], [right]) => (left < right ? 1 : -1))
          .map(([date, values]) => ({
            date: sensor.timezone
              ? utcToZonedTime(date, sensor.timezone)
              : date,
            value: values[0],
            rated: values[1],
          }));
        const fromUnit = data.units[0];

        const fromUnitRated = data.units[1];
        if (!isVolumeFlowRateUnit(fromUnitRated)) {
          throw new Error(`Can only handle discharge but got ${fromUnitRated}`);
        }

        return (
          <tbody>
            {rows.map(({ date, value, rated }) => (
              <tr key={date.getTime()}>
                <td>{date.toLocaleDateString(lang)}</td>
                <td>{date.toLocaleTimeString(lang)}</td>
                <td>
                  {convert(value)
                    .from(fromUnit)
                    .to(toUnit)
                    .toFixed(decimalPlaces(toUnit))}{" "}
                  {formatUnit(toUnit)}
                </td>
                <td>
                  {convert(rated)
                    .from(fromUnitRated)
                    .to(toUnitRated)
                    .toFixed(decimalPlaces(toUnitRated))}{" "}
                  {formatUnit(toUnitRated)}
                </td>
              </tr>
            ))}
          </tbody>
        );
      }
    };

    return (
      <TabularPanel>
        {zoneWarn}
        <Section>
          <Table className="mx-auto is-scrollable">
            <TableHead data={data}></TableHead>
            <TableBody data={data}></TableBody>
          </Table>
        </Section>
      </TabularPanel>
    );
  }
};

export default observer(TabularDataPane);
